import { action, makeObservable, observable, toJS } from 'mobx';
import { fetchResource } from '../../utilities';
import { cloneDeep, pick } from 'lodash';
import BaseService from '../base/BaseService';

/**
 * Service dealing with the creation of quotes for clients (Quoting).
 */
class BaseInsuranceCalculatorService extends BaseService {
  /**
   * Information about the current client filled in the wizard form.
   * @type {{}}
   */
  @observable
  clientData = {};

  @observable
  overThePhone = false;

  constructor(props) {
    super(props);
    makeObservable(this);
  }

  @action
  setClientData = data => {
    this.clientData = data;
  };

  @action
  setOverThePhone = overThePhone => {
    this.overThePhone = overThePhone;
  };

  @action
  getClientData = () => {
    return this.clientData;
  };

  /**
   * Called when the user changes data. Sets it here and persists to browser storage.
   * @param key String - name of the field
   * @param val String - the data
   */
  changeClientData(key, val) {
    this.setClientData({
      ...this.clientData,
      [key]: val,
    });
  }

  /**
   * Makes a load request. Used to load insurance data from the server.
   * @param url
   * @returns {Promise<any>}
   */
  async loadRequest(url) {
    const response = await fetchResource(url);
    return response.json();
  }

  /**
   * Sends client data to the server to run business logic on it and get insurance data.
   * @returns {Promise<void>}
   */
  async calculateRequest(url, propsToSend, bodyPrepend = {}, bodyAppend = {}) {
    const result = await this.makeClientDataRequest(
      url,
      'POST',
      propsToSend,
      bodyPrepend,
      bodyAppend
    );

    return result.json();
  }

  /**
   * Sends insurance confirmation for a quote.
   * @param url
   * @param propsToSend
   * @param bodyPrepend
   * @param bodyAppend
   * @returns {Promise<Response>}
   */
  async confirmRequest(url, propsToSend, bodyPrepend = {}, bodyAppend = {}, quoteId_fix) {
    return this.makeClientDataRequest(url, 'POST', propsToSend, bodyPrepend, bodyAppend, quoteId_fix);
  }

  /**
   * Base function to make requests with client data to the server.
   * @param url
   * @param method
   * @param propsToSend
   * @param bodyPrepend
   * @param bodyAppend
   * @returns {Promise<Response>}
   */
  async makeClientDataRequest(url, method, propsToSend, bodyPrepend = {}, bodyAppend = {}, quoteId_fix) {
    let fix = 'ignoreme';
    if (quoteId_fix > 0) {
      fix = 'quoteId';
    }
    const body = {
      ...bodyPrepend,
      ...cloneDeep(pick(toJS(this.clientData), propsToSend)),
      ...bodyAppend,
      [fix]: quoteId_fix // fix for error "Couldn't process mta with this QUOTE_ID: 15, QUOTE_STATUS : NTU, please select right version of this quote.
    };

    return fetchResource(url, {
      method: method,
      body,
    });
  }
}

export default BaseInsuranceCalculatorService;
